// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { getPricingConfigs, getActiveRates, createPricingConfig, updatePricingConfig, getBanks, getRetailStores, getTier3Operators } from "../lib/api";
import { toast } from "sonner";
import { Plus, DollarSign, CalendarIcon, Check, X, Building2, Store, Network } from "lucide-react";
import { format } from "date-fns";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const Pricing = () => {
  const [configs, setConfigs] = useState([]);
  const [rates, setRates] = useState({});
  const [banks, setBanks] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(new Date());
  const [endDate, setEndDate] = useState(null);
  
  const [formData, setFormData] = useState({
    bank_code: "default",
    pricing_type: "bank",
    fee_per_transaction: "",
    description: ""
  });

  // useRef to guarantee the pricing type is NEVER lost by stale closures
  const pricingTypeRef = useRef("bank");
  const bankCodeRef = useRef("default");

  const fetchData = async () => {
    // 1. Fetch active rates (also auto-creates missing type defaults on server)
    try {
      const ratesRes = await getActiveRates();
      setRates(ratesRes.data || {});
    } catch (e) { console.error("Rates fetch:", e); }
    
    // 2. Fetch configs for table (after rates, so any auto-created defaults show up)
    try {
      const configsRes = await getPricingConfigs();
      setConfigs(Array.isArray(configsRes.data) ? configsRes.data : []);
    } catch (e) { console.error("Configs fetch:", e); }
    
    try { setBanks((await getBanks()).data || []); } catch (e) {}
    try { setRetailers((await getRetailStores()).data || []); } catch (e) {}
    try { setOperators((await getTier3Operators()).data || []); } catch (e) {}
    
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Use refs for pricing_type and bank_code to avoid stale closure issues on mobile
    const currentType = pricingTypeRef.current;
    const currentBankCode = bankCodeRef.current;
    try {
      const payload = {
        bank_code: currentBankCode === "default" ? null : currentBankCode,
        pricing_type: currentType,
        fee_per_transaction: parseFloat(formData.fee_per_transaction),
        effective_date: effectiveDate.toISOString(),
        end_date: endDate ? endDate.toISOString() : null,
        description: formData.description || null
      };
      console.log("[Pricing] Submitting payload:", JSON.stringify(payload));
      await createPricingConfig(payload);
      const typeLabel = currentType === 'bank' ? 'Bank' : currentType === 'retailer' ? 'Retailer' : 'ATM Operator';
      toast.success(`${typeLabel} pricing created — R ${parseFloat(formData.fee_per_transaction).toFixed(2)}`);
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create pricing configuration");
    }
  };

  const handleToggleActive = async (config) => {
    try {
      await updatePricingConfig(config.id, !config.is_active);
      toast.success(`Pricing ${config.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update pricing status");
    }
  };

  const resetForm = () => {
    pricingTypeRef.current = "bank";
    bankCodeRef.current = "default";
    setFormData({
      bank_code: "default",
      pricing_type: "bank",
      fee_per_transaction: "",
      description: ""
    });
    setEffectiveDate(new Date());
    setEndDate(null);
  };

  // Use server-computed rates (primary), fall back to client-side computation from table data
  const computeFromConfigs = (type) => {
    const active = configs.filter(c => {
      const ctype = c.pricing_type || 'bank';
      return (ctype === type || (type === 'atm_operator' && ctype === 'atm_switch')) && c.is_active !== false;
    });
    if (active.length === 0) return null;
    active.sort((a, b) => (b.created_at ? new Date(b.created_at).getTime() : 0) - (a.created_at ? new Date(a.created_at).getTime() : 0));
    return active[0].fee_per_transaction;
  };

  const bankRate = (rates.bank !== null && rates.bank !== undefined) ? rates.bank : computeFromConfigs('bank');
  const retailerRate = (rates.retailer !== null && rates.retailer !== undefined) ? rates.retailer : computeFromConfigs('retailer');
  const operatorRate = (rates.atm_operator !== null && rates.atm_operator !== undefined) ? rates.atm_operator : computeFromConfigs('atm_operator');

  // Get partners list based on pricing type
  const getPartnersList = () => {
    switch (formData.pricing_type) {
      case 'retailer':
        return retailers.map(r => ({ code: r.issuer_code, name: r.display_name }));
      case 'atm_operator':
        return operators.map(o => ({ code: o.operator_id, name: o.operator_name }));
      default:
        return banks.map(b => ({ code: b.bank_code, name: b.bank_name }));
    }
  };

  return (
    <div className="space-y-6" data-testid="pricing-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Pricing Engine</h1>
          <p className="text-muted-foreground">Configure transaction fees by partner type</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="shadow-[0_0_15px_rgba(59,130,246,0.3)]" data-testid="add-pricing-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Pricing Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create Pricing Configuration</DialogTitle>
              <DialogDescription>
                Set transaction fees for banks, retailers, or ATM operators
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Pricing Type</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="pricing-type-select">
                  <button
                    type="button"
                    data-testid="pricing-type-bank"
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                      formData.pricing_type === "bank"
                        ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                        : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/50'
                    }`}
                    onClick={() => {
                      pricingTypeRef.current = "bank";
                      bankCodeRef.current = "default";
                      setFormData(prev => ({ ...prev, pricing_type: "bank", fee_per_transaction: "", bank_code: "default" }));
                    }}
                  >
                    <Building2 className="h-5 w-5" />
                    <span className="text-xs font-medium">Bank</span>
                  </button>
                  <button
                    type="button"
                    data-testid="pricing-type-retailer"
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                      formData.pricing_type === "retailer"
                        ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                        : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/50'
                    }`}
                    onClick={() => {
                      pricingTypeRef.current = "retailer";
                      bankCodeRef.current = "default";
                      setFormData(prev => ({ ...prev, pricing_type: "retailer", fee_per_transaction: "", bank_code: "default" }));
                    }}
                  >
                    <Store className="h-5 w-5" />
                    <span className="text-xs font-medium">Retailer</span>
                  </button>
                  <button
                    type="button"
                    data-testid="pricing-type-atm_operator"
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                      formData.pricing_type === "atm_operator"
                        ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400'
                        : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/50'
                    }`}
                    onClick={() => {
                      pricingTypeRef.current = "atm_operator";
                      bankCodeRef.current = "default";
                      setFormData(prev => ({ ...prev, pricing_type: "atm_operator", fee_per_transaction: "", bank_code: "default" }));
                    }}
                  >
                    <Network className="h-5 w-5" />
                    <span className="text-xs font-medium">ATM Operator</span>
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Specific Partner (Optional)</Label>
                <Select 
                  value={formData.bank_code} 
                  onValueChange={(value) => {
                    bankCodeRef.current = value;
                    setFormData(prev => ({ ...prev, bank_code: value }));
                  }}
                >
                  <SelectTrigger data-testid="pricing-partner-select">
                    <SelectValue placeholder="Default (All)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default (All {formData.pricing_type === 'retailer' ? 'Retailers' : formData.pricing_type === 'atm_operator' ? 'ATM Operators' : 'Banks'})</SelectItem>
                    {getPartnersList().map((partner) => (
                      <SelectItem key={partner.code} value={partner.code}>
                        {partner.code} - {partner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Leave empty for default pricing</p>
              </div>

              <div className="space-y-2">
                <Label>Fee Per Transaction (ZAR)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.fee_per_transaction}
                  onChange={(e) => setFormData(prev => ({ ...prev, fee_per_transaction: e.target.value }))}
                  required
                  data-testid="fee-amount-input"
                />
                <p className="text-xs text-muted-foreground">Charged only on ATM_DISPENSE_SUCCESS</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Effective Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {effectiveDate ? format(effectiveDate, "PP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={effectiveDate}
                        onSelect={setEffectiveDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>End Date (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "PP") : "No end date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Input
                  placeholder="e.g., Standard rate"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  data-testid="pricing-description-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-pricing-btn">
                  Create Pricing Rule
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pricing Tiers Summary - Dynamic from DB */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Bank Pricing */}
        <Card className="bg-card border-border" data-testid="bank-fee-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-500/10">
                <Building2 className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Bank Fee</p>
                <p className="text-2xl font-bold font-mono text-blue-400" data-testid="bank-fee-amount">
                  {bankRate !== null && bankRate !== undefined ? formatCurrency(bankRate) : "R 0.00"}
                </p>
                <p className="text-xs text-muted-foreground">Per successful transaction</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Retailer Pricing */}
        <Card className="bg-card border-border" data-testid="retailer-fee-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-purple-500/10">
                <Store className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Retailer Fee</p>
                <p className="text-2xl font-bold font-mono text-purple-400" data-testid="retailer-fee-amount">
                  {retailerRate !== null && retailerRate !== undefined ? formatCurrency(retailerRate) : "R 0.00"}
                </p>
                <p className="text-xs text-muted-foreground">Per successful transaction</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ATM Operator Pricing */}
        <Card className="bg-card border-border" data-testid="operator-fee-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-cyan-500/10">
                <Network className="h-6 w-6 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">ATM Operator Fee</p>
                <p className="text-2xl font-bold font-mono text-cyan-400" data-testid="operator-fee-amount">
                  {operatorRate !== null && operatorRate !== undefined ? formatCurrency(operatorRate) : "R 0.00"}
                </p>
                <p className="text-xs text-muted-foreground">Per successful transaction</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pricing Configurations Table */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            Pricing Configurations
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Fee</TableHead>
                <TableHead>Effective Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="animate-pulse text-muted-foreground">Loading...</div>
                  </TableCell>
                </TableRow>
              ) : configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <DollarSign className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">No pricing configured</p>
                      <p className="text-sm text-muted-foreground">Add pricing rules above</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                configs.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {config.pricing_type === 'retailer' ? (
                          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                            <Store className="h-3 w-3" />
                            Retailer
                          </span>
                        ) : config.pricing_type === 'atm_switch' || config.pricing_type === 'atm_operator' ? (
                          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                            <Network className="h-3 w-3" />
                            ATM Operator
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                            <Building2 className="h-3 w-3" />
                            Bank
                          </span>
                        )}
                        {config.bank_code && (
                          <code className="font-mono bg-secondary px-2 py-0.5 rounded text-xs">
                            {config.bank_code}
                          </code>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-lg font-bold text-primary">
                        {formatCurrency(config.fee_per_transaction)}
                      </span>
                    </TableCell>
                    <TableCell>{formatDate(config.effective_date)}</TableCell>
                    <TableCell>{formatDate(config.end_date)}</TableCell>
                    <TableCell>
                      <span className="text-muted-foreground">{config.description || "—"}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={config.is_active}
                          onCheckedChange={() => handleToggleActive(config)}
                          data-testid={`toggle-pricing-${config.id}`}
                        />
                        <span className={config.is_active ? "text-green-400" : "text-muted-foreground"}>
                          {config.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-secondary/30 border-border">
        <CardContent className="p-6">
          <h3 className="font-heading font-semibold mb-3">Pricing Rules</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-green-400 mt-0.5 shrink-0" />
              <span>Fees are charged <strong>only</strong> on successful ATM dispense (ATM_DISPENSE_SUCCESS)</span>
            </li>
            <li className="flex items-start gap-2">
              <X className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
              <span>No fees on token generation, expiry, failure, or cancellation</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
              <span><strong>Banks (Issuers):</strong> Fee per successful transaction (configurable per partner)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-purple-400 mt-0.5 shrink-0" />
              <span><strong>Retailers:</strong> Fee per successful transaction (configurable per partner)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-cyan-400 mt-0.5 shrink-0" />
              <span><strong>ATM Operators:</strong> Fee per successful transaction (configurable per partner)</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default Pricing;
